import type { VariationSummaryItem } from '@/lib/api/proofreading';
import {
  codePointLength,
  lineWordToOffset,
  normalizeProofreadingNewlines,
  offsetToLineWord,
} from '@/lib/utils/proofreadingPositions';
import {
  ruleBasedEntryUtf16End,
  validateSyncedCoordinatesForManuscript,
  variationPositionUtf16End,
} from '@/lib/utils/proofreadingCoordinateValidation';
import type { ProofreadingIssueStatus } from '@/types/proofreading';
import type {
  RuleBasedErrorEntryStored,
  VariationSummaryItemStored,
  VariationSummaryPositionStored,
} from '@/lib/utils/proofreadingStoredTypes';

export type TextChange = {
  manuscriptId: string;
  startUtf16: number;
  oldUtf16Len: number;
  newText: string;
};

type RangeRelation = 'before' | 'after' | 'outer' | 'inner' | 'partial';

export type SyncCoordsResult = {
  results: RuleBasedErrorEntryStored[];
  variationSummary: VariationSummaryItemStored[];
  changed: boolean;
};

export type SyncCoordsOptions = {
  issueStatuses?: Map<string, ProofreadingIssueStatus>;
  /** 提案適用などホットパスでは全件検証を省略する */
  skipValidation?: boolean;
};

export { offsetToLineWord, lineWordToOffset } from '@/lib/utils/proofreadingPositions';

export function computeReplacementHunks(
  before: string,
  after: string,
  manuscriptId: string,
): TextChange[] {
  if (before === after) {
    return [];
  }

  const hunks: TextChange[] = [];
  let bi = 0;
  let ai = 0;

  while (bi < before.length || ai < after.length) {
    while (bi < before.length && ai < after.length && before[bi] === after[ai]) {
      bi += 1;
      ai += 1;
    }

    if (bi >= before.length && ai >= after.length) {
      break;
    }

    const startUtf16 = bi;
    const [biEnd, aiEnd] = findNextSyncPoint(bi, ai, before, after);

    hunks.push({
      manuscriptId,
      startUtf16,
      oldUtf16Len: biEnd - bi,
      newText: after.slice(ai, aiEnd),
    });

    bi = biEnd;
    ai = aiEnd;
  }

  return hunks;
}

/** findNextSyncPoint の offset ループ探索上限（探索コスト用。区間長とは別） */
export const FIND_NEXT_SYNC_SEARCH_CAP = 2000;

function findNextSyncPoint(
  bi: number,
  ai: number,
  before: string,
  after: string,
): [number, number] {
  let biEnd = before.length;
  let aiEnd = after.length;

  while (biEnd > bi && aiEnd > ai && before[biEnd - 1] === after[aiEnd - 1]) {
    biEnd -= 1;
    aiEnd -= 1;
  }

  const searchBeforeLen = biEnd - bi;
  const searchAfterLen = aiEnd - ai;
  if (searchBeforeLen === 0 && searchAfterLen === 0) {
    return [bi, ai];
  }

  const maxOffset = Math.min(
    FIND_NEXT_SYNC_SEARCH_CAP,
    Math.max(searchBeforeLen, searchAfterLen) + 1,
  );

  for (let offset = 1; offset <= maxOffset; offset += 1) {
    for (let delBefore = 0; delBefore <= offset; delBefore += 1) {
      const insAfter = offset - delBefore;
      const candidateBiEnd = bi + delBefore;
      const candidateAiEnd = ai + insAfter;
      if (candidateBiEnd <= biEnd && candidateAiEnd <= aiEnd) {
        let common = 0;
        while (
          candidateBiEnd + common < before.length
          && candidateAiEnd + common < after.length
          && before[candidateBiEnd + common] === after[candidateAiEnd + common]
        ) {
          common += 1;
        }

        const syncedToEnd = candidateBiEnd + common >= biEnd && candidateAiEnd + common >= aiEnd;
        if (common > 0 || syncedToEnd) {
          return [candidateBiEnd, candidateAiEnd];
        }
      }
    }
  }

  return [biEnd, aiEnd];
}

export function applyTextChange(text: string, change: TextChange): string {
  return text.slice(0, change.startUtf16)
    + change.newText
    + text.slice(change.startUtf16 + change.oldUtf16Len);
}

function classifyRange(
  entryStart: number,
  entryEnd: number,
  changeStart: number,
  changeEnd: number,
): RangeRelation {
  if (entryEnd <= changeStart) return 'before';
  if (entryStart >= changeEnd) return 'after';
  if (entryStart <= changeStart && entryEnd >= changeEnd) return 'outer';
  if (entryStart >= changeStart && entryEnd <= changeEnd) return 'inner';
  return 'partial';
}

function isExactRangeReplacement(
  entryStart: number,
  entryEnd: number,
  change: TextChange,
): boolean {
  return (
    entryStart === change.startUtf16
    && entryEnd === change.startUtf16 + change.oldUtf16Len
  );
}

function withEntryPositionFromUtf16(
  entry: RuleBasedErrorEntryStored,
  startUtf16: number,
  textAfter: string,
  targetTextOverride?: string,
): RuleBasedErrorEntryStored {
  const startPos = offsetToLineWord(textAfter, startUtf16);
  const targetText = targetTextOverride ?? entry.target_text;
  const lastUtf16 = Math.min(
    textAfter.length - 1,
    Math.max(startUtf16, startUtf16 + Math.max(0, targetText.length - 1)),
  );
  const endPos = offsetToLineWord(textAfter, lastUtf16);
  return {
    ...entry,
    start_line: startPos.line,
    start_word: startPos.word,
    end_line: endPos.line,
    end_word: endPos.word,
    ...(targetTextOverride != null
      ? {
          target_text: targetTextOverride,
          length: codePointLength(targetTextOverride),
        }
      : {}),
  };
}

function withVariationPositionFromUtf16(
  pos: VariationSummaryPositionStored,
  startUtf16: number,
  textAfter: string,
  spanText: string,
): VariationSummaryPositionStored {
  const startPos = offsetToLineWord(textAfter, startUtf16);
  const lastUtf16 = Math.min(
    textAfter.length - 1,
    Math.max(startUtf16, startUtf16 + Math.max(0, spanText.length - 1)),
  );
  const endPos = offsetToLineWord(textAfter, lastUtf16);
  return {
    ...pos,
    start_line: startPos.line,
    start_word: startPos.word,
    end_line: endPos.line,
    end_word: endPos.word,
  };
}

function resolveEntryStart(
  textBefore: string,
  line: number,
  word: number,
): number | null {
  return lineWordToOffset(textBefore, line, word);
}

function shiftRuleBasedResults(
  textBefore: string,
  textAfter: string,
  change: TextChange,
  results: RuleBasedErrorEntryStored[],
): RuleBasedErrorEntryStored[] {
  const changeStart = change.startUtf16;
  const changeEnd = change.startUtf16 + change.oldUtf16Len;
  const delta = change.newText.length - change.oldUtf16Len;
  const oldSlice = textBefore.slice(changeStart, changeEnd);
  const cpDelta = codePointLength(change.newText) - codePointLength(oldSlice);

  return results.map((entry) => {
    if (entry.manuscript_id !== change.manuscriptId) {
      return entry;
    }

    const next: RuleBasedErrorEntryStored = { ...entry };
    if (next.coord_invalid) {
      return next;
    }

    const entryStart = resolveEntryStart(textBefore, entry.start_line, entry.start_word);
    if (entryStart === null) {
      next.coord_invalid = true;
      return next;
    }
    const entryEnd = ruleBasedEntryUtf16End(textBefore, entry, entryStart);
    const relation = classifyRange(entryStart, entryEnd, changeStart, changeEnd);

    switch (relation) {
      case 'before':
        return next;
      case 'after':
        return withEntryPositionFromUtf16(next, entryStart + delta, textAfter);
      case 'outer':
        if (isExactRangeReplacement(entryStart, entryEnd, change)) {
          return withEntryPositionFromUtf16(
            next,
            entryStart,
            textAfter,
            change.newText,
          );
        }
        return withEntryPositionFromUtf16(
          { ...next, length: Math.max(0, next.length + cpDelta) },
          entryStart,
          textAfter,
        );
      case 'inner':
        next.coord_invalid = true;
        return next;
      case 'partial':
        next.coord_invalid = true;
        return next;
      default:
        return next;
    }
  });
}

function shiftVariationSummary(
  textBefore: string,
  textAfter: string,
  change: TextChange,
  variationSummary: VariationSummaryItemStored[],
): VariationSummaryItemStored[] {
  const changeStart = change.startUtf16;
  const changeEnd = change.startUtf16 + change.oldUtf16Len;
  const delta = change.newText.length - change.oldUtf16Len;
  const oldSlice = textBefore.slice(changeStart, changeEnd);
  const cpDelta = codePointLength(change.newText) - codePointLength(oldSlice);

  return variationSummary.map((item) => ({
    ...item,
    variants: item.variants.map((variant) => ({
      ...variant,
      position: (variant.position ?? []).map((pos) => {
        if (pos.manuscript_id !== change.manuscriptId) {
          return pos;
        }

        const next: VariationSummaryPositionStored = { ...pos };
        if (next.coord_invalid) {
          return next;
        }

        const entryStart = resolveEntryStart(textBefore, pos.start_line, pos.start_word);
        if (entryStart === null) {
          next.coord_invalid = true;
          return next;
        }
        const entryEnd = variationPositionUtf16End(variant.text, entryStart);
        const relation = classifyRange(entryStart, entryEnd, changeStart, changeEnd);

        switch (relation) {
          case 'before':
            return next;
          case 'after':
            return withVariationPositionFromUtf16(
              next,
              entryStart + delta,
              textAfter,
              variant.text,
            );
          case 'outer':
            if (isExactRangeReplacement(entryStart, entryEnd, change)) {
              return withVariationPositionFromUtf16(
                { ...next, length: codePointLength(change.newText) },
                entryStart,
                textAfter,
                change.newText,
              );
            }
            return withVariationPositionFromUtf16(
              { ...next, length: Math.max(0, next.length + cpDelta) },
              entryStart,
              textAfter,
              variant.text,
            );
          case 'inner':
            next.coord_invalid = true;
            return next;
          case 'partial':
            next.coord_invalid = true;
            return next;
          default:
            return next;
        }
      }),
    })),
  }));
}

export function shiftCoordinatesForTextChange(params: {
  textBefore: string;
  textAfter: string;
  change: TextChange;
  results: RuleBasedErrorEntryStored[];
  variationSummary: VariationSummaryItemStored[];
}): {
  results: RuleBasedErrorEntryStored[];
  variationSummary: VariationSummaryItemStored[];
} {
  return {
    results: shiftRuleBasedResults(
      params.textBefore,
      params.textAfter,
      params.change,
      params.results,
    ),
    variationSummary: shiftVariationSummary(
      params.textBefore,
      params.textAfter,
      params.change,
      params.variationSummary,
    ),
  };
}

function finalizeSyncedCoordinates(params: {
  manuscriptId: string;
  textAfter: string;
  results: RuleBasedErrorEntryStored[];
  variationSummary: VariationSummaryItemStored[];
  options?: SyncCoordsOptions;
}): {
  results: RuleBasedErrorEntryStored[];
  variationSummary: VariationSummaryItemStored[];
} {
  const {
    manuscriptId,
    textAfter,
    results,
    variationSummary,
    options,
  } = params;

  if (options?.skipValidation) {
    return { results, variationSummary };
  }

  return validateSyncedCoordinatesForManuscript({
    manuscriptId,
    text: textAfter,
    results,
    variationSummary,
    issueStatuses: options?.issueStatuses,
  });
}

export function syncCoordsFromKnownChange(params: {
  manuscriptId: string;
  textBefore: string;
  textAfter: string;
  change: TextChange;
  results: RuleBasedErrorEntryStored[];
  variationSummary: VariationSummaryItem[] | VariationSummaryItemStored[];
  options?: SyncCoordsOptions;
}): SyncCoordsResult {
  const {
    manuscriptId,
    textBefore: rawTextBefore,
    textAfter: rawTextAfter,
    change,
    results: initialResults,
    variationSummary: initialVariationSummary,
    options,
  } = params;

  const textBefore = normalizeProofreadingNewlines(rawTextBefore);
  const textAfter = normalizeProofreadingNewlines(rawTextAfter);

  if (change.manuscriptId !== manuscriptId) {
    const validated = finalizeSyncedCoordinates({
      manuscriptId,
      textAfter,
      results: initialResults,
      variationSummary: initialVariationSummary as VariationSummaryItemStored[],
      options,
    });
    return {
      results: validated.results,
      variationSummary: validated.variationSummary,
      changed: false,
    };
  }

  const expectedAfter = applyTextChange(textBefore, change);
  if (expectedAfter !== textAfter) {
    return syncCoordsToCurrentText({
      manuscriptId,
      textBefore: rawTextBefore,
      textAfter: rawTextAfter,
      results: initialResults,
      variationSummary: initialVariationSummary,
      options,
    });
  }

  const { results: shiftedResults, variationSummary: shiftedVariationSummary } =
    shiftCoordinatesForTextChange({
      textBefore,
      textAfter,
      change,
      results: initialResults,
      variationSummary: initialVariationSummary as VariationSummaryItemStored[],
    });

  const validated = finalizeSyncedCoordinates({
    manuscriptId,
    textAfter,
    results: shiftedResults,
    variationSummary: shiftedVariationSummary,
    options,
  });

  return {
    results: validated.results,
    variationSummary: validated.variationSummary,
    changed: true,
  };
}

export function syncCoordsToCurrentText(params: {
  manuscriptId: string;
  textBefore: string;
  textAfter: string;
  results: RuleBasedErrorEntryStored[];
  variationSummary: VariationSummaryItem[] | VariationSummaryItemStored[];
  options?: SyncCoordsOptions;
}): SyncCoordsResult {
  const {
    manuscriptId,
    textBefore: rawTextBefore,
    textAfter: rawTextAfter,
    results: initialResults,
    variationSummary: initialVariationSummary,
    options,
  } = params;
  const initialTextBefore = normalizeProofreadingNewlines(rawTextBefore);
  const textAfter = normalizeProofreadingNewlines(rawTextAfter);
  const hunks = computeReplacementHunks(
    initialTextBefore,
    textAfter,
    manuscriptId,
  );

  if (hunks.length === 0) {
    const validated = finalizeSyncedCoordinates({
      manuscriptId,
      textAfter,
      results: initialResults,
      variationSummary: initialVariationSummary as VariationSummaryItemStored[],
      options,
    });
    return {
      results: validated.results,
      variationSummary: validated.variationSummary,
      changed: false,
    };
  }

  let currentBefore: string = initialTextBefore;
  let results = initialResults;
  let variationSummary = initialVariationSummary as VariationSummaryItemStored[];

  hunks.forEach((hunk) => {
    const currentAfter = applyTextChange(currentBefore, hunk);
    const { results: shiftedResults, variationSummary: shiftedVariationSummary } = shiftCoordinatesForTextChange({
      textBefore: currentBefore,
      textAfter: currentAfter,
      change: hunk,
      results,
      variationSummary,
    });
    results = shiftedResults;
    variationSummary = shiftedVariationSummary;
    currentBefore = currentAfter;
  });

  const validated = finalizeSyncedCoordinates({
    manuscriptId,
    textAfter,
    results,
    variationSummary,
    options,
  });

  return {
    results: validated.results,
    variationSummary: validated.variationSummary,
    changed: true,
  };
}
